import React from 'react'
import { Light, AsleepFilled } from '@carbon/icons-react'
import { useTheme } from 'next-themes'
import { cn } from '../../utils/cn'
import { Spinner } from './spinner'
import { Tooltip } from './tooltip'

export interface ThemeToggleProps {
  /**
   * Optional persistence hook. Called with the new mode after the theme is
   * flipped; if it rejects, the toggle reverts to the previous mode. Leave
   * undefined for a purely local toggle.
   */
  onModeChange?: (mode: 'light' | 'dark') => void | Promise<void>
  /** Tooltip placement (defaults to `right`). */
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
}

/**
 * Dark / light mode switch. Reads and sets the theme via `next-themes`, so it
 * is self-contained anywhere a `ThemeProvider` is mounted. Persistence is
 * intentionally not baked in — pass `onModeChange` to sync the choice somewhere
 * (e.g. the user's account preferences).
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  onModeChange,
  tooltipPlacement = 'right',
  className,
}) => {
  const { resolvedTheme, setTheme } = useTheme()
  const colorMode = resolvedTheme === 'dark' ? 'dark' : 'light'
  const [isSaving, setIsSaving] = React.useState(false)
  const [mounted, setMounted] = React.useState(false)

  // Avoid hydration mismatch: render a stable icon until mounted.
  React.useEffect(() => setMounted(true), [])

  const isLightMode = colorMode === 'light'
  const nextMode: 'light' | 'dark' = isLightMode ? 'dark' : 'light'
  const tooltipLabel = isLightMode ? 'Switch to dark mode' : 'Switch to light mode'

  const handleToggle = async () => {
    if (isSaving) return
    const previousMode = colorMode
    setTheme(nextMode)

    if (!onModeChange) return
    setIsSaving(true)
    try {
      await onModeChange(nextMode)
    } catch (error) {
      setTheme(previousMode)
      console.error('Failed to update theme preference:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const icon = isSaving ? (
    <Spinner size="sm" />
  ) : isLightMode ? (
    <AsleepFilled size={20} />
  ) : (
    <Light size={20} />
  )

  return (
    <Tooltip label={tooltipLabel} placement={tooltipPlacement} openDelay={300}>
      <button
        type="button"
        aria-label={tooltipLabel}
        onClick={handleToggle}
        disabled={isSaving}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-subtle hover:text-content-primary disabled:opacity-70',
          className,
        )}
      >
        {mounted ? icon : <AsleepFilled size={20} />}
      </button>
    </Tooltip>
  )
}

ThemeToggle.displayName = 'ThemeToggle'
